// pages/Trades.jsx - Complete with filtering, grouping, and summaries
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import TradeDetailsModal from '../components/TradeDetailsModal';
import { formatCurrency, formatGroupDate } from '../utils';

const Trades = () => {
  const [trades, setTrades] = useState([]);
  const [filteredTrades, setFilteredTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Filter states
  const [viewMode, setViewMode] = useState('today'); // today, week, month, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date()); // Track current date for navigation
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDays, setExpandedDays] = useState(new Set());

  useEffect(() => {
    fetchTrades();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [trades, viewMode, startDate, endDate, currentDate, searchQuery]);

  const fetchTrades = async () => {
    setLoading(true);
    try {
    const response = await axios.get(`${API_BASE_PATH}/trades-grouped`);
    const tradesData = response.data || [];
    setTrades(tradesData);
      
      // Set default dates to show latest trades
      if (tradesData.length > 0) {
        const validDates = tradesData
          .map(t => t.first_buy_ts || t.last_sell_ts)
          .filter(Boolean)
          .map(ts => {
            try {
              const date = new Date(ts);
              return isNaN(date.getTime()) ? null : date;
            } catch (e) {
              return null;
            }
          })
          .filter(Boolean)
          .sort()
          .reverse();
        
        if (validDates.length > 0) {
          const latestDate = validDates[0].toISOString().split('T')[0];
          setStartDate(latestDate);
          setEndDate(latestDate);
        }
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTradeDate = (trade) => {
    // For grouped trades, use date_closed or date_opened
    const dateStr = trade.date_closed || trade.date_opened;
    if (!dateStr) return null;
    
    try {
      // The dates are already in YYYY-MM-DD format from the backend
      const date = new Date(dateStr + 'T00:00:00Z'); // Add time to avoid timezone issues
      if (isNaN(date.getTime())) return null;
      return dateStr; // Return the string directly since it's already in the right format
    } catch (e) {
      return null;
    }
  };

  const applyFilters = () => {
    let filtered = [...trades].filter(t => getTradeDate(t) !== null);

    // Apply view mode
    const refDate = new Date(currentDate);
    refDate.setHours(0, 0, 0, 0);

    if (viewMode === 'today') {
      const dateStr = refDate.toISOString().split('T')[0];
      filtered = filtered.filter(t => {
        const tradeDate = getTradeDate(t);
        return tradeDate === dateStr;
      });
      setStartDate(dateStr);
      setEndDate(dateStr);
    } else if (viewMode === 'week') {
      // Get Monday of the current week
      const dayOfWeek = refDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const monday = new Date(refDate);
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days, else go to Monday
      monday.setDate(refDate.getDate() + diff);

      // Get Friday of the current week or today if before Friday
      const friday = new Date(monday);
      friday.setDate(monday.getDate() + 4); // Friday is 4 days after Monday

      // Use the earlier of Friday or today
      const endOfWeek = refDate < friday ? refDate : friday;

      const mondayStr = monday.toISOString().split('T')[0];
      const endStr = endOfWeek.toISOString().split('T')[0];

      filtered = filtered.filter(t => {
        const tradeDate = getTradeDate(t);
        return tradeDate && tradeDate >= mondayStr && tradeDate <= endStr;
      });
      setStartDate(mondayStr);
      setEndDate(endStr);
    } else if (viewMode === 'month') {
      // Get first day of the month
      const firstDay = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
      // Get last day of the month
      const lastDay = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);

      const firstDayStr = firstDay.toISOString().split('T')[0];
      const lastDayStr = lastDay.toISOString().split('T')[0];

      filtered = filtered.filter(t => {
        const tradeDate = getTradeDate(t);
        return tradeDate && tradeDate >= firstDayStr && tradeDate <= lastDayStr;
      });
      setStartDate(firstDayStr);
      setEndDate(lastDayStr);
    } else if (viewMode === 'custom') {
      // FIXED: Only apply custom filter if both dates are set
      if (startDate && endDate) {
        filtered = filtered.filter(t => {
          const tradeDate = getTradeDate(t);
          return tradeDate && tradeDate >= startDate && tradeDate <= endDate;
        });
      }
      // If dates aren't set yet, show all trades
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.option_name?.toLowerCase().includes(query)
      );
    }

    setFilteredTrades(filtered);

    // Auto-expand all days initially
    if (expandedDays.size === 0 && filtered.length > 0) {
      const dates = new Set(
        filtered.map(t => getTradeDate(t)).filter(d => d !== null)
      );
      setExpandedDays(dates);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setCurrentDate(new Date()); // Reset to today when changing view mode
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'today') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'custom') {
      // In custom mode, go back by the same number of days as the current range
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
      start.setDate(start.getDate() - daysDiff);
      end.setDate(end.getDate() - daysDiff);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
      setCurrentDate(start);
      return; // Exit early since we've already set the dates
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'today') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'custom') {
      // In custom mode, go forward by the same number of days as the current range
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
      start.setDate(start.getDate() + daysDiff);
      end.setDate(end.getDate() + daysDiff);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
      setCurrentDate(start);
      return; // Exit early since we've already set the dates
    }
    setCurrentDate(newDate);
  };

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value);
      setCurrentDate(new Date(value));
    } else {
      setEndDate(value);
      setCurrentDate(new Date(value));
    }
    setViewMode('custom');
  };

  const toggleDayExpansion = (dateKey) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dateKey)) {
      newExpanded.delete(dateKey);
    } else {
      newExpanded.add(dateKey);
    }
    setExpandedDays(newExpanded);
  };

  const expandAll = () => {
    const dates = new Set(
      filteredTrades.map(t => new Date(t.last_sell_ts || t.first_buy_ts).toISOString().split('T')[0])
    );
    setExpandedDays(dates);
  };

  const collapseAll = () => {
    setExpandedDays(new Set());
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    const summary = filteredTrades.reduce((acc, trade) => {
      const totalFees = (trade.buy_fees || 0) + (trade.sell_fees || 0);
      const pnl = (trade.total_sell_price || 0) - (trade.total_buy_price || 0) - totalFees;
      
      acc.count += 1;
      acc.totalValue += trade.trade_value || 0;
      acc.totalFees += totalFees;
      acc.totalPnL += pnl;
      
      return acc;
    }, { count: 0, totalValue: 0, totalFees: 0, totalPnL: 0 });
    
    return summary;
  };

  // Group trades by date - using helper function
  const groupedTrades = (() => {
    const groups = {};
    filteredTrades.forEach(trade => {
      const dateKey = getTradeDate(trade);
      if (!dateKey) return; // Skip trades with invalid dates
      
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(trade);
    });
    
    const sortedKeys = Object.keys(groups).sort().reverse(); // Newest first
    return sortedKeys.map(dateKey => ({
      dateKey,
      items: groups[dateKey]
    }));
  })();

  const summary = calculateSummary();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading trades...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trades-page">
      {/* Integrated Header with Summary */}
      <div style={{ 
        marginBottom: '16px',
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden'
      }}>
        {/* Top Section: Title, View Mode, and Summary */}
        <div style={{
          background: 'var(--primary-gradient)',
          padding: '12px 20px',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Left: Title */}
          <div>
            <h1 style={{ 
              fontSize: '20px', 
              fontWeight: 700, 
              margin: 0,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-exchange-alt"></i>
              Trades
            </h1>
            <p style={{ 
              fontSize: '11px', 
              color: 'rgba(255, 255, 255, 0.9)', 
              margin: '2px 0 0 0' 
            }}>
              {summary.count} closed positions
            </p>
          </div>

          {/* Center: View Mode Buttons */}
          <div className="view-mode-buttons" style={{ display: 'flex', gap: '4px' }}>
            <button 
              className={`btn-view-mode ${viewMode === 'today' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('today')}
              style={{
                background: viewMode === 'today' ? 'white' : 'rgba(255, 255, 255, 0.2)',
                color: viewMode === 'today' ? 'var(--primary-color)' : 'white',
                border: 'none',
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: 600
              }}
            >
              Today
            </button>
            <button 
              className={`btn-view-mode ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('week')}
              style={{
                background: viewMode === 'week' ? 'white' : 'rgba(255, 255, 255, 0.2)',
                color: viewMode === 'week' ? 'var(--primary-color)' : 'white',
                border: 'none',
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: 600
              }}
            >
              Week
            </button>
            <button 
              className={`btn-view-mode ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('month')}
              style={{
                background: viewMode === 'month' ? 'white' : 'rgba(255, 255, 255, 0.2)',
                color: viewMode === 'month' ? 'var(--primary-color)' : 'white',
                border: 'none',
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: 600
              }}
            >
              Month
            </button>
          </div>

          {/* Right: Summary Stats */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '20px',
            color: 'white'
          }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: 600 }}>TRADE VALUE</div>
              <div style={{ fontSize: '16px', fontWeight: 700 }}>{formatCurrency(summary.totalValue)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: 600 }}>FEES</div>
              <div style={{ fontSize: '16px', fontWeight: 700 }}>{formatCurrency(summary.totalFees)}</div>
            </div>
            <div style={{ 
              textAlign: 'right',
              background: 'rgba(255, 255, 255, 0.25)',
              padding: '6px 16px',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px' }}>TOTAL P&L</div>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>{formatCurrency(summary.totalPnL)}</div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Date Controls and Search */}
        <div style={{ 
          padding: '12px 20px',
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          background: 'var(--bg-tertiary)'
        }}>
          {/* Date Navigation */}
          <button
            className="btn-nav-date"
            onClick={handlePrevious}
            style={{ padding: '6px 10px', fontSize: '13px' }}
          >
            &lt;
          </button>

          <input
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange('start', e.target.value)}
            className="form-control"
            style={{ width: '130px', padding: '6px 8px', fontSize: '12px' }}
          />

          <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 500 }}>to</span>

          <input
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange('end', e.target.value)}
            className="form-control"
            style={{ width: '130px', padding: '6px 8px', fontSize: '12px' }}
          />

          <button
            className="btn-nav-date"
            onClick={handleNext}
            style={{ padding: '6px 10px', fontSize: '13px' }}
          >
            &gt;
          </button>

          {/* Divider */}
          <div style={{ width: '1px', height: '24px', background: 'var(--border-color)' }}></div>

          {/* Search */}
          <input 
            type="text"
            placeholder="Search by option name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
            style={{ 
              flex: 1,
              minWidth: '180px',
              maxWidth: '280px',
              padding: '6px 12px',
              fontSize: '13px'
            }}
          />

          {/* Expand/Collapse */}
          <button 
            className="btn btn-secondary" 
            style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }} 
            onClick={collapseAll}
          >
            Collapse All
          </button>
          <button 
            className="btn btn-secondary" 
            style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }} 
            onClick={expandAll}
          >
            Expand All
          </button>
        </div>
      </div>

      {/* Trades Table */}
      {filteredTrades.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-exchange-alt empty-state-icon"></i>
          <h3 className="empty-state-title">No trades found</h3>
          <p className="empty-state-description">
            {searchQuery ? 'Try adjusting your search' : 'No trades in this date range'}
          </p>
        </div>
      ) : (
        <div>
          {/* Single Table Header */}
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '8px 12px',
            paddingLeft: '54px',
            borderBottom: '2px solid var(--border-color)',
            marginBottom: '8px',
            borderRadius: '4px 4px 0 0'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '200px 70px 90px 90px 70px 90px 110px 70px 50px',
              gap: '8px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--text-secondary)',
              letterSpacing: '0.5px'
            }}>
              <div>Symbol</div>
              <div>Qty</div>
              <div>Avg Buy</div>
              <div>Avg Sell</div>
              <div>Fees</div>
              <div>Total Value</div>
              <div>P&L</div>
              <div>Duration</div>
              <div>Info</div>
            </div>
          </div>

          {/* Grouped Trades */}
          <div className="trades-grouped">
            {groupedTrades.map(({ dateKey, items }) => {
              const isExpanded = expandedDays.has(dateKey);
              const dayPnL = items.reduce((sum, trade) => {
                const fees = (trade.buy_fees || 0) + (trade.sell_fees || 0);
                return sum + ((trade.total_sell_price || 0) - (trade.total_buy_price || 0) - fees);
              }, 0);

              return (
                <div key={dateKey} className="trade-day-group" style={{ marginBottom: '2px' }}>
                  {/* Day Header Row */}
                  <div
                    onClick={() => toggleDayExpansion(dateKey)}
                    style={{
                      cursor: 'pointer',
                      padding: '6px 12px',
                      background: dayPnL >= 0 ? '#e6ffe6' : '#ffe6e6',
                      borderLeft: `3px solid ${dayPnL >= 0 ? '#1a7f37' : '#b91c1c'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '13px',
                      fontWeight: 600,
                      transition: 'background 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <i
                        className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
                        style={{ fontSize: '10px', color: '#666' }}
                      ></i>
                      <span style={{ color: '#333' }}>{formatGroupDate(dateKey)}</span>
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        ({items.length} {items.length === 1 ? 'trade' : 'trades'})
                      </span>
                    </div>
                    <span style={{
                      fontWeight: 700,
                      color: dayPnL >= 0 ? '#1a7f37' : '#b91c1c'
                    }}>
                      {dayPnL >= 0 ? '+' : ''}{formatCurrency(dayPnL)}
                    </span>
                  </div>

                  {/* Trade Rows */}
                  {isExpanded && items.map((trade, idx) => {
                    // Debug: Log first trade to see available fields
                    if (idx === 0 && dateKey === groupedTrades[0]?.dateKey) {
                      console.log('Sample trade object:', trade);
                    }

                    const totalFees = (trade.buy_fees || 0) + (trade.sell_fees || 0);
                    const pnl = (trade.total_sell_price || 0) - (trade.total_buy_price || 0) - totalFees;
                    const pnlPercent = trade.total_buy_price ? ((pnl / trade.total_buy_price) * 100).toFixed(2) : 0;
                    const isProfitable = pnl >= 0;

                    // Parse option name
                    const parts = trade.option_name?.split(' ') || [];
                    const symbol = parts[0] || '';
                    const strikeType = parts[1] || '';
                    const expiration = parts[2] || '';

                    // Calculate averages (divide by 100 for options pricing)
                    const avgBuy = trade.quantity_bought ? (trade.total_buy_price / trade.quantity_bought / 100) : 0;
                    const avgSell = trade.quantity_sold ? (trade.total_sell_price / trade.quantity_sold / 100) : 0;

                    // Format duration from API (duration_hours field)
                    let timeDisplay = '-';
                    if (trade.duration_hours != null) {
                      const totalMinutes = Math.round(trade.duration_hours * 60);
                      if (totalMinutes >= 60) {
                        const hours = Math.floor(totalMinutes / 60);
                        const minutes = totalMinutes % 60;
                        timeDisplay = minutes > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}` : `${hours}:00`;
                      } else {
                        timeDisplay = `${totalMinutes}`;
                      }
                    }

                    return (
                      <div
                        key={trade.id}
                        onClick={() => setSelectedTrade(trade)}
                        style={{
                          cursor: 'pointer',
                          padding: '6px 12px',
                          paddingLeft: '54px',
                          background: '#fff',
                          borderBottom: '1px solid #e5e5e5',
                          display: 'grid',
                          gridTemplateColumns: '200px 70px 90px 90px 70px 90px 110px 70px 50px',
                          gap: '8px',
                          alignItems: 'center',
                          fontSize: '13px',
                          transition: 'background 0.1s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9f9f9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontWeight: 700, fontSize: '14px' }}>{symbol}</span>
                          <span style={{ fontSize: '11px', color: '#666' }}>
                            {strikeType} • {expiration}
                          </span>
                        </div>
                        <span>{trade.quantity_bought || trade.quantity_sold || 0}</span>
                        <span style={{ fontSize: '12px' }}>{formatCurrency(avgBuy)}</span>
                        <span style={{ fontSize: '12px' }}>{formatCurrency(avgSell)}</span>
                        <span style={{ color: '#b91c1c', fontSize: '12px' }}>{formatCurrency(totalFees)}</span>
                        <span style={{ fontSize: '12px' }}>{formatCurrency(trade.total_buy_price || 0)}</span>
                        <span style={{
                          fontWeight: 600,
                          fontSize: '12px',
                          color: isProfitable ? '#1a7f37' : '#b91c1c'
                        }}>
                          {formatCurrency(pnl)} ({pnlPercent}%)
                        </span>
                        <span style={{ fontSize: '12px', color: '#666' }}>{timeDisplay}</span>
                        <button
                          className="btn-table-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTrade(trade);
                          }}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: 'var(--primary-color)'
                          }}
                        >
                          <i className="fas fa-info-circle"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trade Details Modal */}
      {selectedTrade && (
        <TradeDetailsModal 
          trade={selectedTrade} 
          onClose={() => setSelectedTrade(null)}
        />
      )}
    </div>
  );
};

export default Trades;